/**
 * Formatting utilities for All Farm View
 * Provides relative time formatting and volume formatting for irrigation data
 */

var dayjs = require('./dayjsConfig');

var SHORT_MONTH_KEYS = [
  'shorts_months_january',
  'shorts_months_february',
  'shorts_months_march',
  'shorts_months_april',
  'shorts_months_may',
  'shorts_months_june',
  'shorts_months_july',
  'shorts_months_august',
  'shorts_months_september',
  'shorts_months_october',
  'shorts_months_november',
  'shorts_months_december',
];

/**
 * Formats a time difference as a relative "ago" string.
 * Shared by formatRelativeTime and formatTransmitterUpdate.
 * @param {number} diffMinutes - Difference in minutes
 * @param {number} diffHours - Difference in hours
 * @param {number} diffDays - Difference in days
 * @param {function|null} t - Optional translation function
 * @returns {string} Relative time string (e.g. "5min ago", "3h ago", "2d ago", "1 week ago")
 */
var formatTimeAgo = function (diffMinutes, diffHours, diffDays, t) {
  if (diffHours < 1) {
    var minutes = Math.max(1, diffMinutes);
    return minutes + 'min ' + (t ? t('ago') : 'ago');
  }

  if (diffHours < 24) {
    return diffHours + 'h ' + (t ? t('ago') : 'ago');
  }

  if (diffDays < 7) {
    var remainingHours = diffHours - diffDays * 24;
    if (remainingHours > 0) {
      return diffDays + 'd ' + remainingHours + 'h ' + (t ? t('ago') : 'ago');
    }
    return diffDays + 'd ' + (t ? t('ago') : 'ago');
  }

  var weeks = Math.floor(diffDays / 7);
  return weeks + 'w ' + (t ? t('ago') : 'ago');
};

/**
 * Formats an ISO timestamp as relative time
 * @param {string|null} isoTimestamp - ISO 8601 timestamp string
 * @returns {string} Relative time string or 'N/A'
 */
var formatRelativeTime = function (isoTimestamp, t) {
  if (!isoTimestamp) {
    return 'N/A';
  }

  var localDate = dayjs(isoTimestamp);
  if (!localDate.isValid()) {
    return 'N/A';
  }

  // Use UTC for consistent time difference calculation across timezones
  var dateUtc = localDate.utc();
  var nowUtc = dayjs().utc();
  var diffMs = nowUtc.diff(dateUtc);
  var diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 0) {
    return localDate.format('MMM D, HH:mm');
  }

  var diffMinutes = Math.floor(diffMs / (1000 * 60));
  var diffDays = Math.floor(diffHours / 24);

  return formatTimeAgo(diffMinutes, Math.floor(diffHours), diffDays, t || null);
};

/**
 * Formats a volume value in cubic meters
 * @param {number|null} value - Volume in cubic meters
 * @returns {string} Formatted string with thousands separator or 'N/A'
 */
var formatM3 = function (value) {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  // Format with thousands separator and 1 decimal place
  var formattedValue = value.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return formattedValue + ' m\u00B3';
};

/**
 * Formats a number with thousands separator
 * @param {number|null} value - Number to format
 * @param {number} decimals - Number of decimal places (default 0)
 * @returns {string} Formatted string or 'N/A'
 */
var formatNumber = function (value, decimals) {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  var decimalPlaces = decimals !== undefined ? decimals : 0;

  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

var {
  isDurationBasedIrrigationType,
  isDurationBasedDisplay,
  normalizeLengthUnitName,
  isInchLengthUnit,
  isHoursUnit,
} = require('@managers/allFarmViewFormatters');

/**
 * Formats a performance value with its unit
 * @param {number|null} value - Performance value
 * @param {string|null} units - Unit type ('INCH', 'MM', etc.)
 * @returns {string} Formatted string with unit or 'N/A'
 */
var formatPerformanceValue = function (value, units) {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  var isInch = units === 'INCH';
  var decimals = isInch ? 2 : 1;
  var fixedValue = value.toFixed(decimals);
  var parts = fixedValue.split('.');
  var intPart = parseInt(parts[0], 10).toLocaleString('en-US');
  var formattedValue = parts.length > 1 ? intPart + '.' + parts[1] : intPart;

  var unitSymbol = '';
  if (isInch) {
    unitSymbol = '″';
  } else if (units === 'MM') {
    unitSymbol = ' mm';
  } else if (units === 'CM') {
    unitSymbol = ' cm';
  } else if (units === 'M3') {
    unitSymbol = ' m\u00B3';
  } else if (units) {
    unitSymbol = ' ' + units.toLowerCase();
  }

  return formattedValue + unitSymbol;
};

/**
 * Formats irrigation duration from start and end timestamps
 * @param {string|null} startTimestamp - ISO 8601 start timestamp
 * @param {string|null} endTimestamp - ISO 8601 end timestamp
 * @returns {string} Duration string like "2hr" or "2hr 45min" or null if can't calculate
 */
var formatIrrigationDuration = function (startTimestamp, endTimestamp, t) {
  if (!startTimestamp || !endTimestamp) {
    return null;
  }

  var start = dayjs(startTimestamp);
  var end = dayjs(endTimestamp);

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  var diffMs = end.diff(start);
  if (diffMs < 0) {
    return null;
  }

  var totalMinutes = Math.floor(diffMs / (1000 * 60));
  var hours = Math.floor(totalMinutes / 60);
  var minutes = totalMinutes % 60;

  if (hours === 0) {
    return minutes + 'min';
  }

  if (minutes === 0) {
    return hours + 'hr';
  }

  return hours + 'hr ' + minutes + 'min';
};

/**
 * Formats irrigation amount with unit (mm or inch)
 * @param {number|null} amount - Irrigation amount
 * @param {string} unit - Unit of measurement (mm, inch)
 * @returns {string|null} Formatted string like "4.2 mm" or null
 */
var formatIrrigationAmount = function (amount, unit) {
  if (
    amount === null ||
    amount === undefined ||
    typeof amount !== 'number' ||
    isNaN(amount)
  ) {
    return null;
  }
  var unitLower = (unit || 'mm').toLowerCase();
  var isInch = unitLower === 'inch' || unitLower === 'in';
  var decimals = isInch ? 2 : 1;
  var formatted = parseFloat(amount.toFixed(decimals));
  var unitSuffix = unit || 'mm';
  return formatted + ' ' + unitSuffix;
};

/**
 * Formats a time for display (e.g., "14:30")
 * @param {string|null} isoTimestamp - ISO 8601 timestamp
 * @returns {string} Time string or 'N/A'
 */
var formatTime = function (isoTimestamp) {
  if (!isoTimestamp) {
    return 'N/A';
  }

  var date = dayjs(isoTimestamp);
  if (!date.isValid()) {
    return 'N/A';
  }

  return date.format('HH:mm');
};

/**
 * Formats a timestamp with relative day and time (e.g., "Today 14:30", "Yesterday 14:30", "2 days ago")
 * @param {string|null} isoTimestamp - ISO 8601 timestamp
 * @param {function} t - Translation function from react-i18next
 * @returns {string} Relative day with time string or 'N/A'
 */
var formatRelativeDay = function (isoTimestamp, t) {
  if (!isoTimestamp) {
    return 'N/A';
  }

  var date = dayjs(isoTimestamp);
  if (!date.isValid()) {
    return 'N/A';
  }

  var now = dayjs();
  var startOfToday = now.startOf('day');
  var startOfDate = date.startOf('day');
  var diffDays = startOfToday.diff(startOfDate, 'day');

  var timeStr = date.format('HH:mm');

  // Helper to capitalize first letter
  var capitalize = function (str) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Use translation function if provided, fallback to English
  // Capitalize since these appear at the start of a phrase
  var todayText = t ? capitalize(t('today')) : 'Today';
  var yesterdayText = t ? capitalize(t('yesterday')) : 'Yesterday';
  var daysAgoText = t ? t('days_ago_suffix') : 'days ago';

  if (diffDays === 0) {
    return todayText + ' ' + timeStr;
  }

  if (diffDays === 1) {
    return yesterdayText + ' ' + timeStr;
  }

  if (diffDays < 7) {
    return diffDays + ' ' + daysAgoText;
  }

  return date.format('MMM D, HH:mm');
};

/**
 * Formats a transmitter update timestamp in user-friendly relative format
 * @param {string|null} isoTimestamp - ISO 8601 timestamp
 * @param {function} t - Translation function from react-i18next
 * @returns {string} Formatted string: "Updated X hour/s ago", "Updated X day/s ago", "Updated X week/s ago"
 */
var formatTransmitterUpdate = function (isoTimestamp, t) {
  if (!isoTimestamp) {
    return 'N/A';
  }

  var date = dayjs(isoTimestamp);
  if (!date.isValid()) {
    return 'N/A';
  }

  var now = dayjs();
  var diffMinutes = now.diff(date, 'minute');
  var diffHours = now.diff(date, 'hour');
  var diffDays = now.diff(date, 'day');

  var prefix = t ? t('last_synced') : 'Last synced:';
  return prefix + ' ' + formatTimeAgo(diffMinutes, diffHours, diffDays, t);
};

/**
 * Formats recommendation text based on frequency, unit type, and planner mode
 * @param {number} amount - Irrigation amount
 * @param {string} unit - Unit of measurement (mm, inch, hours)
 * @param {number} frequencyDays - Days between irrigations
 * @param {number} timesPerWeek - Times per week
 * @param {string} mode - Planner mode ('weekly' or 'interval')
 * @param {Function} t - Translation function
 * @returns {Object} { amountText, frequencyText }
 */
var formatRecommendation = function (
  amount,
  unit,
  frequencyDays,
  timesPerWeek,
  mode,
  t,
) {
  if (!amount || amount <= 0) {
    return { amountText: null, frequencyText: null };
  }

  // Split value + unit (performance row uses 14px value + 12px unit on baseline).
  var unitLower = unit ? unit.toLowerCase() : '';
  var amountValue;
  var amountUnit = null;
  if (unitLower === 'hours') {
    var rounded = Math.round(amount * 2) / 2;
    var hourLabel =
      rounded === 1 ? (t?.('hour') ?? 'hour') : (t?.('hours') ?? 'hours');
    amountValue = rounded + ' ' + hourLabel;
  } else if (unitLower === 'inch' || unitLower === 'in') {
    amountValue = String(parseFloat(amount.toFixed(2)));
    amountUnit = '\u2033';
  } else {
    amountValue = String(parseFloat(amount.toFixed(1)));
    amountUnit = 'mm';
  }
  var amountText = amountValue + (amountUnit || '');

  var withFrequency = function (frequencyText) {
    return {
      amountText: amountText,
      amountValue: amountValue,
      amountUnit: amountUnit,
      frequencyText: frequencyText,
    };
  };

  if (mode === 'weekly' && timesPerWeek > 0) {
    var timesLabel =
      t?.('new_planner_recommendation_times_a_week') ?? 'times a week';
    return withFrequency(timesPerWeek + ' ' + timesLabel);
  }

  if (mode !== 'weekly' && frequencyDays > 0) {
    if (frequencyDays === 1) {
      var everyDayLabel =
        t?.('new_planner_recommendation_format_every_day') ?? 'every day';
      return withFrequency(everyDayLabel);
    }
    var everyLabel = t?.('new_planner_recommendation_every') ?? 'every';
    var dayLabel = t?.('new_planner_recommendation_days') ?? 'days';
    return withFrequency(everyLabel + ' ' + frequencyDays + ' ' + dayLabel);
  }

  return withFrequency(null);
};

module.exports = {
  formatRelativeTime: formatRelativeTime,
  formatM3: formatM3,
  formatNumber: formatNumber,
  isDurationBasedIrrigationType: isDurationBasedIrrigationType,
  isDurationBasedDisplay: isDurationBasedDisplay,
  normalizeLengthUnitName: normalizeLengthUnitName,
  isInchLengthUnit: isInchLengthUnit,
  isHoursUnit: isHoursUnit,
  formatPerformanceValue: formatPerformanceValue,
  formatIrrigationDuration: formatIrrigationDuration,
  formatIrrigationAmount: formatIrrigationAmount,
  formatTime: formatTime,
  formatRelativeDay: formatRelativeDay,
  formatTransmitterUpdate: formatTransmitterUpdate,
  formatRecommendation: formatRecommendation,
  SHORT_MONTH_KEYS: SHORT_MONTH_KEYS,
};
